import { useNavigate } from 'react-router-dom';
import { RoomWithMembers, RoomWithMembersAndStages, User } from '../common/types';
import { useMemo, useState } from 'react';
import { Modal } from './Modal';
import { UserIcon } from './user/UserIcon';
import { ShareButton } from './ShareButton';
import { RoomStageProgress } from './stages/RoomStageProgress';
import { RoomStageDetailedProgress } from './stages/RoomStageDetailedProgress';

export type InfoButtonProps = {
  room: RoomWithMembersAndStages;
  messageAuthors: User[];
  authorColors: { [key: string]: string };
};

export function InfoButton(props: InfoButtonProps) {
  const members = props.room.members;
  const authors = props.messageAuthors;
  const memberSet = useMemo(() => new Set(members.map((m) => m.userUuid)), [members]);
  const nonMemberAuthors = useMemo(
    () => authors.filter((a) => !memberSet.has(a.userUuid)),
    [authors, memberSet],
  );
  const authorColors = props.authorColors;
  const room = props.room;

  const [open, setOpen] = useState<boolean>(false);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="absolute top-4 right-4 z-50 flex items-center space-x-2 text-white border border-slate-700 rounded-full p-2 bg-slate-700 hover:bg-slate-600 transition-colors duration-300 opacity-50 hover:opacity-100"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24px"
          height="24px"
          viewBox="0 0 45.999 45.999"
          fill="currentColor"
        >
          <g>
            <g>
              <path
                d="M25.999,33c0,1.657-1.343,3-3,3s-3-1.343-3-3V21c0-1.657,1.343-3,3-3s3,1.343,3,3V33z
                  M22.946,15.872c-1.728,0-2.88-1.224-2.844-2.735c-0.036-1.584,1.116-2.771,2.879-2.771
                  c1.764,0,2.88,1.188,2.917,2.771C25.897,14.648,24.746,15.872,22.946,15.872z"
              />
            </g>
          </g>
        </svg>
      </button>

      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        size="max-w-lg w-full"
        color="bg-[#1e2a38]"
      >
        <div className="pt-8">
          <div className="room-card rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-white text-2xl font-bold">Room #{room.number}</h1>
              <ShareButton />
            </div>

            <RoomStageDetailedProgress stages={room.stages} />

            <div className="mb-4">
              <h4 className="text-gray-300 mb-2">Current members:</h4>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {members.map((member) => (
                  <div key={member.userUuid} className="flex items-center gap-2">
                    <UserIcon user={member} color={authorColors[member.userUuid]} />
                    <p className="text-white">{member.name}</p>
                  </div>
                ))}
              </div>
            </div>

            <hr className="border-gray-700 my-4" />

            <div className="mb-4">
              <h4 className="text-gray-300 mb-2">Other participants:</h4>
              <div className="flex flex-wrap gap-2">
                {nonMemberAuthors.map((member) => (
                  <div key={member.userUuid} className="flex items-center gap-2">
                    <UserIcon user={member} color={authorColors[member.userUuid]} />
                    <p className="text-white">{member.name}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
